/**
 * Wire-format types shared with the backend.
 *
 * We mirror the canonical event envelope here in hand-authored form
 * (rather than depending only on the generated bundle) so that
 * application code keeps compiling even if ``scripts/gen-types.sh``
 * hasn't been run yet.  The generated file in
 * ``types.generated.ts`` is the authoritative payload-shape source --
 * we use it for narrowing inside event handlers via ``EventOfType``.
 */

export type EventActor =
  | 'qa'
  | 'sa'
  | 'fa'
  | 'researcher'
  | 'rm'
  | 'ea'
  | 'pm'
  | 'risk_officer'
  | 'center_book'
  | 'discipline_gate'
  | 'policy_updater'
  | 'leaderboard_keeper'
  | 'system'
  | 'trader';

export type EventMood =
  | 'idle'
  | 'thinking'
  | 'confident'
  | 'worried'
  | 'excited'
  | 'angry'
  | 'proud'
  | 'sad'
  | 'frozen'
  | 'celebrating';

export type EventIntensity = 'low' | 'medium' | 'high' | 'critical';

export type EventCamera =
  | 'stay'
  | 'focus_on_subject'
  | 'focus_on_target'
  | 'wide'
  | 'building_overview'
  | 'floor_overview'
  | 'party_hall';

export interface SceneHint {
  actor?: EventActor | string;
  target?: string;
  mood?: EventMood | string;
  intensity?: EventIntensity | string;
  audio_cue?: string;
  camera?: EventCamera | string;
  duration_ms?: number;
}

/**
 * Wire envelope.  ``data`` is left open here; per-type narrowing lives
 * in {@link EventOfType} which leans on ``types.generated.ts``.
 *
 * ``id`` is the Redis Stream cursor of the stream this envelope was
 * read from — NOT stable across reads from different streams (events:all
 * vs events:<subject> give two different values for the same logical
 * event).  Use ``origin_id`` for client-side dedup; falls back to ``id``
 * when absent (envelopes published before the field existed).
 */
export interface Envelope<TData = Record<string, unknown>> {
  id: string;
  origin_id?: string | null;
  ts: string;
  subject: string;
  type: string;
  version: number;
  trace_id?: string | null;
  data: TData;
  scene_hint?: SceneHint | null;
  narrative?: string | null;
}

export type AnyEnvelope = Envelope<Record<string, unknown>>;

/**
 * Canonical event type identifiers.  Stable across versions; never
 * rename, only deprecate -- mirrors ``pod_platform.events.types.EventType``.
 */
export const EventType = {
  // Cycle lifecycle
  CYCLE_STARTED: 'cycle.started',
  CYCLE_COMPLETED: 'cycle.completed',
  CYCLE_SKIPPED_MARKET_CLOSED: 'cycle.skipped_market_closed',

  // Agent layer
  AGENT_THINKING_STARTED: 'agent.thinking_started',
  AGENT_THINKING_COMPLETED: 'agent.thinking_completed',
  AGENT_SIGNAL_EMITTED: 'agent.signal_emitted',
  AGENT_IDLE_ACTION: 'agent.idle_action',

  // PM
  PM_BRIEFING_BUILT: 'pm.briefing_built',
  PM_DECISION_MADE: 'pm.decision_made',
  PM_NO_TRADE_DECISION: 'pm.no_trade_decision',

  // Risk
  RISK_VERDICT_RECEIVED: 'risk.verdict_received',
  RISK_STATE_CHANGED: 'risk.state_changed',

  // Discipline
  DISCIPLINE_FLAG_RAISED: 'discipline.flag_raised',
  DISCIPLINE_GATE_BLOCKED_ORDER: 'discipline.gate_blocked_order',

  // Center book
  CENTERBOOK_OVERLAY_APPLIED: 'centerbook.overlay_applied',
  CENTERBOOK_ALLOCATION_CHANGED: 'centerbook.allocation_changed',

  // Orders
  ORDER_SUBMITTED: 'order.submitted',
  ORDER_FILLED: 'order.filled',
  ORDER_REJECTED: 'order.rejected',
  ORDER_RETRIED: 'order.retried',

  // Positions
  POSITION_OPENED: 'position.opened',
  POSITION_CLOSED: 'position.closed',
  POSITION_ADJUSTED: 'position.adjusted',

  // Live per-pod NAV ticks (every ~10 min, no LLM round-trip required)
  POD_NAV_TICK: 'pod.nav_tick',

  // Utility / policy
  UTILITY_RECORDED: 'utility.recorded',
  POLICY_NO_CHANGE: 'policy.no_change',
  POLICY_SHADOW_RECOMMENDED: 'policy.shadow_recommended',
  POLICY_APPLIED: 'policy.applied',
  POLICY_ROLLED_BACK: 'policy.rolled_back',

  // Rooms
  ROOM_ENTERED: 'room.entered',
  ROOM_EXITED: 'room.exited',

  // Leaderboard + celebration
  LEADERBOARD_COMPUTED: 'leaderboard.computed',
  LEADERBOARD_RANK_CHANGED: 'leaderboard.rank_changed',
  CELEBRATION_WEEKLY_STARTED: 'celebration.weekly_started',
  CELEBRATION_PODIUM: 'celebration.podium',
  CELEBRATION_WEEKLY_ENDED: 'celebration.weekly_ended',

  // Building-wide
  BUILDING_DAILY_CLOSE_STARTED: 'building.daily_close_started',
  BUILDING_DAILY_CLOSE_COMPLETED: 'building.daily_close_completed',
  BUILDING_BEAT_TICK: 'building.beat_tick',
  BUILDING_FACTOR_CROWD_DETECTED: 'building.factor_crowd_detected',
  BUILDING_FACTOR_CROWD_RELIEVED: 'building.factor_crowd_relieved',

  // Atmospheric loop (driven by BuildingAtmosphereService, 5-min ticks)
  BUILDING_WEATHER_CHANGED: 'building.weather_changed',
  BUILDING_SUN_POSITION: 'building.sun_position',
  BUILDING_SESSION_OPENED: 'building.session_opened',
  BUILDING_SESSION_CLOSED: 'building.session_closed',
  BUILDING_SESSION_WARNING: 'building.session_warning',

  // News intelligence (external producer: NewsIntelligence service via
  // PodPlatform's external-publisher endpoint). Single ``news`` subject
  // for the global feed; per-pod relevance is decided in subscribers.
  NEWS_EVENT_INGESTED: 'news.event_ingested',
  NEWS_TICKER_SENTIMENT_SHIFT: 'news.ticker_sentiment_shift',
  NEWS_DIGEST_PUBLISHED: 'news.digest_published',
} as const;

export type EventTypeValue = (typeof EventType)[keyof typeof EventType];

/** Routing key helpers. */
export const Subject = {
  SYSTEM: 'system',
  BUILDING: 'building',
  LEADERBOARD: 'leaderboard',
  NEWS: 'news',
  pod: (podId: string) => `pod:${podId}` as const,
  /** ``"pod:*"`` matches all pod subjects; ``"*"`` matches everything. */
  matches: (subject: string, filterExpr: string): boolean => {
    if (filterExpr === '*') return true;
    if (filterExpr.endsWith(':*')) return subject.startsWith(filterExpr.slice(0, -1));
    return subject === filterExpr;
  },
} as const;

export function podIdFromSubject(subject: string): string | null {
  return subject.startsWith('pod:') ? subject.slice('pod:'.length) : null;
}
